import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import IconSearch from '../../assets/icons/IconSearch';
import IconX from '../../assets/icons/IconX';
import IconCheckFilled from '../../assets/icons/IconCheckFilled';
import SearchEmptyState from '../SearchEmptyState/SearchEmptyState';
import { LazyImage } from '../LazyImage/LazyImage';
import {
  resolveDropdownPlacement,
  resolveDropdownOffsetX,
  resolveDropdownMaxHeight,
  DropdownPlacement,
} from './resolveDropdownPlacement';
import type {
  SelectionDropdownCloseReason,
  SelectionDropdownItem,
  SelectionDropdownProps,
} from './selectionDropdownTypes';
import './SelectionDropdown.sass';

let generatedListboxIds = 0;

const hideBrokenIcon = (event: React.SyntheticEvent<HTMLImageElement>) => {
  event.currentTarget.style.display = 'none';
};

/**
 * Desktop picker panel built to the Backoffice "Dropdown / Dropdown container": a panel anchored
 * to its positioned parent with a search box that stays put while only the list scrolls.
 * Presentation and interaction only — callers own the items, the filtering and the selection.
 */
function SelectionDropdown<T>(props: SelectionDropdownProps<T>) {
  const {
    isOpen,
    onClose,
    items,
    onSelect,
    searchQuery,
    onSearchQueryChange,
    texts,
    showIcons = true,
    closeOnOutsideClick = true,
    outsideClickIgnoreRefs,
    className = '',
    listboxId: listboxIdProp,
    centred = false,
  } = props;

  const [isInputFocused, setIsInputFocused] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [placement, setPlacement] = useState<DropdownPlacement>('below');
  const [offsetX, setOffsetX] = useState<number | null>(null);
  const [maxHeight, setMaxHeight] = useState<number | null>(null);
  const placementRef = useRef<DropdownPlacement>('below');
  const panelRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const previouslyFocusedRef = useRef<HTMLElement | null>(null);
  const scrollActiveIntoViewRef = useRef(false);
  const generatedIdRef = useRef<string | null>(null);
  const onCloseRef = useRef(onClose);
  const ignoreRefsRef = useRef(outsideClickIgnoreRefs);

  onCloseRef.current = onClose;
  ignoreRefsRef.current = outsideClickIgnoreRefs;

  if (!generatedIdRef.current) {
    generatedListboxIds += 1;
    generatedIdRef.current = `selection-dropdown-listbox-${generatedListboxIds}`;
  }
  const listboxId = listboxIdProp || generatedIdRef.current;
  const getOptionId = (index: number) => `${listboxId}-option-${index}`;

  const restoreFocus = () => {
    const element = previouslyFocusedRef.current;
    if (element && document.contains(element)) element.focus();
  };

  const close = (reason: SelectionDropdownCloseReason) => {
    if (reason !== 'outside') restoreFocus();
    onCloseRef.current(reason);
  };
  const closeRef = useRef(close);
  closeRef.current = close;

  const handleSelect = (item: SelectionDropdownItem<T>) => {
    onSelect(item);
    close('select');
  };

  const moveActiveIndex = (delta: 1 | -1) => {
    if (!items.length) return;
    scrollActiveIntoViewRef.current = true;
    setActiveIndex((current) => Math.min(Math.max(current + delta, 0), items.length - 1));
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      moveActiveIndex(event.key === 'ArrowDown' ? 1 : -1);
      return;
    }
    if (event.key === 'Enter' && event.target === inputRef.current) {
      const item =
        activeIndex >= 0 ? items[activeIndex] : items.length === 1 ? items[0] : undefined;
      if (item) {
        event.preventDefault();
        handleSelect(item);
      }
    }
  };

  const handlePanelMouseDown = (event: React.MouseEvent<HTMLDivElement>) => {
    // Keep the keyboard in the search box while clicking rows or the scrollbar; clicks still fire.
    if (event.button === 0 && event.target !== inputRef.current) event.preventDefault();
  };

  useLayoutEffect(() => {
    if (!isOpen) {
      setPlacement('below');
      setOffsetX(null);
      setMaxHeight(null);
      placementRef.current = 'below';
      return undefined;
    }
    const measure = () => {
      const panel = panelRef.current;
      const anchor = panel?.parentElement;
      if (!panel || !anchor) return;
      const next =
        placementRef.current === 'above' ? 'above' : resolveDropdownPlacement({ anchor, panel });
      placementRef.current = next;
      setPlacement(next);
      setMaxHeight(resolveDropdownMaxHeight({ anchor, placement: next }));
      setOffsetX(centred ? resolveDropdownOffsetX({ anchor, panel }) : null);
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [isOpen, centred, items.length]);

  useEffect(() => {
    if (!isOpen) return undefined;
    previouslyFocusedRef.current = document.activeElement as HTMLElement | null;
    setActiveIndex(-1);
    inputRef.current?.focus();
    const reassertFocus = requestAnimationFrame(() => inputRef.current?.focus());
    return () => {
      cancelAnimationFrame(reassertFocus);
      previouslyFocusedRef.current = null;
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') closeRef.current('escape');
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !closeOnOutsideClick) return undefined;
    const handlePointerDown = (event: Event) => {
      const target = event.target as Node | null;
      const panel = panelRef.current;
      if (!target || !panel || panel.contains(target)) return;
      if (ignoreRefsRef.current?.some((ref) => ref.current?.contains(target))) return;
      // Blur first so the field that owns the picker gets its usual blur before the panel unmounts.
      const activeElement = document.activeElement as HTMLElement | null;
      if (activeElement && panel.contains(activeElement)) activeElement.blur();
      closeRef.current('outside');
    };
    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('touchstart', handlePointerDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('touchstart', handlePointerDown);
    };
  }, [isOpen, closeOnOutsideClick]);

  useEffect(() => {
    if (activeIndex < 0 || !scrollActiveIntoViewRef.current) return;
    scrollActiveIntoViewRef.current = false;
    const option = document.getElementById(`${listboxId}-option-${activeIndex}`);
    if (option && typeof option.scrollIntoView === 'function')
      option.scrollIntoView({ block: 'nearest' });
  }, [activeIndex, listboxId]);

  if (!isOpen) return null;

  const panelClassName = ['selection-dropdown', placement === 'above' ? '_above' : '', className]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      ref={panelRef}
      className={panelClassName}
      style={{
        ...(offsetX === null ? {} : { left: offsetX, right: 'auto' }),
        ...(maxHeight === null ? {} : { maxHeight }),
      }}
      onKeyDown={handleKeyDown}
      onMouseDown={handlePanelMouseDown}
    >
      <div className="selection-dropdown__search">
        <div
          className={`selection-dropdown__search-input ${isInputFocused ? '_focused' : ''} ${searchQuery ? '_filled' : ''}`}
        >
          <IconSearch />
          <input
            ref={inputRef}
            className="selection-dropdown__search-field"
            type="text"
            role="combobox"
            aria-expanded={true}
            aria-autocomplete="list"
            aria-controls={listboxId}
            aria-activedescendant={activeIndex >= 0 ? getOptionId(activeIndex) : undefined}
            placeholder={texts.searchPlaceholder}
            value={searchQuery}
            onChange={(event) => {
              onSearchQueryChange(event.target.value);
              setActiveIndex(-1);
            }}
            onFocus={() => setIsInputFocused(true)}
            onBlur={() => setIsInputFocused(false)}
          />
          {searchQuery && (
            <button
              type="button"
              className="selection-dropdown__search-clear"
              aria-label="Clear search"
              onMouseDown={(event) => {
                event.preventDefault();
                event.stopPropagation();
              }}
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                onSearchQueryChange('');
                setActiveIndex(-1);
                inputRef.current?.focus();
              }}
            >
              <IconX />
            </button>
          )}
        </div>
      </div>

      <div
        id={listboxId}
        role="listbox"
        aria-label={texts.listLabel}
        className="selection-dropdown__list"
      >
        {items.length === 0 ? (
          <div className="selection-dropdown__empty">
            <SearchEmptyState title={texts.emptyTitle} subtitle={texts.emptySubtitle} />
          </div>
        ) : (
          items.map((item, index) => (
            <button
              type="button"
              key={item.key}
              id={getOptionId(index)}
              role="option"
              aria-selected={item.selected}
              className={`selection-dropdown__item ${item.selected ? '_selected' : ''} ${index === activeIndex ? '_active' : ''} ${showIcons ? '' : '_no-icon'}`}
              onClick={() => handleSelect(item)}
              onMouseEnter={() => setActiveIndex(index)}
            >
              {showIcons && (
                <span className="selection-dropdown__item-icon" aria-hidden="true">
                  {item.iconSrc && <LazyImage src={item.iconSrc} alt="" onError={hideBrokenIcon} />}
                </span>
              )}
              <span className="selection-dropdown__item-label">{item.label}</span>
              {item.detail && (
                <span className="selection-dropdown__item-detail">{item.detail}</span>
              )}
              {item.selected && (
                <span className="selection-dropdown__item-checkmark" aria-hidden="true">
                  <IconCheckFilled />
                </span>
              )}
            </button>
          ))
        )}
      </div>
    </div>
  );
}

export default SelectionDropdown;
